import { defineMessages } from "./define.js";

/**
 * Сквозное шифрование, как оно объясняется человеку.
 *
 * Здесь нет ни одного слова вроде «ключевого материала» и «режима»: всё, что
 * говорится на этих экранах, человек читает в тот момент, когда решает судьбу
 * своей переписки за несколько лет. Поэтому цена названа прямо — «не откроет
 * никто», а не «восстановление может быть затруднено».
 */
export const encryption = defineMessages("encryption", {
  /* ------------------------------------------------------- карточка настроек */

  title: { ru: "Шифрование", en: "Encryption" },

  stateOff: { ru: "Выключено", en: "Off" },
  stateOn: { ru: "Включено", en: "On" },

  offNote: {
    ru: "Сейчас записи хранятся на сервере в открытом виде: их защищает канал связи и доступ к серверу, но не шифрование.",
    en: "Right now everything is stored on the server in the clear: the connection and server access protect it, encryption does not.",
  },
  onNote: {
    ru: "Списки, переписка и календарь шифруются на устройстве. Сервер хранит их, но прочитать не может.",
    en: "Lists, chat and calendar are encrypted on the device. The server keeps them and cannot read them.",
  },
  ownerOnly: {
    ru: "Включить шифрование может только владелец пространства.",
    en: "Only the owner of the space can turn encryption on.",
  },

  enable: { ru: "Включить шифрование", en: "Turn encryption on" },
  enabling: { ru: "Включаем…", en: "Turning it on…" },
  enableFailed: {
    ru: "Не удалось включить шифрование. Ничего не изменилось — попробуйте ещё раз.",
    en: "Encryption could not be turned on. Nothing changed — try again.",
  },

  /* --------------------------------------------- экран сразу после регистрации

     Человек только что завёл своё пространство, и оно пустое. Разговор здесь
     идёт двумя половинами — «если оставить как есть» и «если включить», — и обе
     названы одинаково честно: у первой есть своя выгода, у второй своя цена.
     Уговаривать нельзя ни в одну сторону: человек должен принять решение, а не
     угадать, какую кнопку от него ждут. */

  welcomeTitle: { ru: "Прежде чем начать", en: "Before you start" },
  welcomeLede: {
    ru: "В пространстве пока пусто — и это лучший момент решить про шифрование: перешифровывать нечего.",
    en: "Your space is still empty — which makes this the best moment to decide about encryption: there is nothing to re-encrypt yet.",
  },

  welcomeOffTitle: { ru: "Если оставить как есть", en: "If you leave it as it is" },
  welcomeOffBody: {
    ru: "Записи лежат на сервере в открытом виде: их защищает канал связи и доступ к серверу, но не шифрование. Зато потеря телефона ничего не стирает — всё на сервере: войдёте с другого устройства, и записи на месте.",
    en: "Everything is kept on the server in the clear: the connection and access to the server protect it, encryption does not. In exchange, losing your phone erases nothing — it is all on the server, so you sign in from another device and find it where you left it.",
  },

  welcomeOnTitle: { ru: "Если включить", en: "If you turn it on" },
  welcomeOnBody: {
    ru: "Содержимое не прочитает никто, включая того, у кого сервер. Но появляется обязанность: код восстановления нужно сохранить. Если потеряны все устройства, а кода нет, содержимое не вернёт никто.",
    en: "Nobody can read what is inside — not even whoever holds the server. But it brings a duty: the recovery code has to be kept. If every device is lost and the code is gone, nobody can bring the content back.",
  },

  /* Стоит отдельной строкой под обеими половинами, а не внутри второй: это
     свойство самого решения, и внутри «если включить» оно перевесило бы чашу. */
  welcomeForever: {
    ru: "Шифрование включают один раз: выключить обратно нельзя.",
    en: "Encryption is turned on once: it cannot be switched back off.",
  },

  welcomeLater: { ru: "Позже", en: "Later" },
  welcomeLaterNote: {
    ru: "Позже — тоже можно, в настройках. Только тогда написанное придётся перешифровать.",
    en: "Later works too, from Settings. Only then whatever is already written has to be re-encrypted.",
  },

  welcomeCodeLede: {
    ru: "Шифрование включено. Осталось одно — сохранить код восстановления.",
    en: "Encryption is on. One thing is left: keep the recovery code.",
  },

  /* ------------------------------------------------------------ шаг 1: цена */

  warnTitle: { ru: "Это решение без обратного хода", en: "There is no way back from this" },
  warnServer: {
    ru: "После включения сервер перестанет понимать, что у вас написано: он будет хранить нечитаемые записи и отдавать их вашим устройствам.",
    en: "Once it is on, the server stops understanding what you write: it keeps unreadable records and hands them back to your devices.",
  },
  warnForever: {
    ru: "Выключить шифрование обратно нельзя.",
    en: "Encryption cannot be switched back off.",
  },
  warnLoss: {
    ru: "Вы получите код восстановления. Если код потерян и ни на одном устройстве не осталось ключа, содержимое не восстановит никто — ни мы, ни человек с полным доступом к серверу.",
    en: "You will be given a recovery code. If the code is lost and no device still holds the key, nobody can bring the content back — not us, not somebody with full access to the server.",
  },
  warnConfirm: { ru: "Включить", en: "Turn it on" },

  /* -------------------------------------------------- шаг 2: код восстановления */

  codeTitle: { ru: "Код восстановления", en: "Recovery code" },
  codeOnce: {
    ru: "Он показывается один раз. Мы его не храним и показать ещё раз не сможем.",
    en: "It is shown once. We do not keep it and cannot show it again.",
  },
  codeWhere: {
    ru: "Положите его в менеджер паролей или запишите на бумаге и уберите туда, где лежат документы.",
    en: "Put it in a password manager, or write it down and keep it where your documents are.",
  },
  codeAria: { ru: "Код восстановления пространства", en: "The space recovery code" },
  copy: { ru: "Скопировать", en: "Copy" },
  copied: { ru: "Скопировано", en: "Copied" },
  copyManually: {
    ru: "Скопируйте код вручную",
    en: "Copy the code by hand",
  },
  codeSaved: { ru: "Я сохранил", en: "I have saved it" },

  /* ----------------------------------------------------------- перешифровка */

  catchUpTitle: { ru: "Написанное раньше", en: "What was written before" },
  catchUpRunning: {
    ru: "Перешифровано {done} из {total}",
    en: "Re-encrypted {done} of {total}",
  },
  catchUpLeft: {
    ru: {
      one: "Осталась {count} запись в открытом виде.",
      few: "Осталось {count} записи в открытом виде.",
      many: "Осталось {count} записей в открытом виде.",
      other: "Осталось {count} записи в открытом виде.",
    },
    en: {
      one: "{count} record is still in the clear.",
      other: "{count} records are still in the clear.",
    },
  },
  catchUpResumable: {
    ru: "Это нормально: проход можно продолжить в любой момент, он начнётся с того же места.",
    en: "That is fine: the pass can be picked up at any time and carries on where it stopped.",
  },
  catchUpResume: { ru: "Продолжить", en: "Carry on" },
  catchUpDone: {
    ru: "Всё написанное раньше перешифровано.",
    en: "Everything written before is re-encrypted now.",
  },
  catchUpFailed: {
    ru: "Перешифровка прервалась. Ничего не потеряно — нажмите «Продолжить».",
    en: "The pass stopped short. Nothing is lost — press “Carry on”.",
  },

  /* -------------------------------------- ключ по QR: своё второе устройство */

  /**
   * Здесь квадрат делает ровно одно дело: переносит ключ на второй телефон того
   * же человека. Звать нового — не отсюда: приглашение вместе со своим квадратом
   * живёт выше, в блоке «Приглашение», и слова здесь названы так, чтобы два
   * квадрата не читались как одно и то же в двух местах.
   */
  showKey: { ru: "Показать ключ для второго устройства", en: "Show the key for a second device" },
  hideKey: { ru: "Убрать ключ с экрана", en: "Take the key off the screen" },
  qrTitle: {
    ru: "Наведите камеру своего второго телефона",
    en: "Point your own second phone's camera here",
  },
  qrOwnOnly: {
    ru: "Это для вашего второго телефона: вы уже участник, приглашение вам не нужно. Нового человека зовите приглашением — оно выше, там же и свой квадрат.",
    en: "This is for a second phone of your own: you are already a member and need no invitation. To bring somebody new in, use the invitation above — it has a square of its own.",
  },
  qrWarning: {
    ru: "Этот квадрат — и есть ключ от пространства. Показывайте его только человеку, которого пускаете внутрь, и не пересылайте перепиской и снимком экрана.",
    en: "This square is the key to the space. Show it only to someone you are letting in — never send it in a chat or as a screenshot.",
  },
  qrNoKey: {
    ru: "Ключа на этом устройстве нет, показывать нечего.",
    en: "This device holds no key, so there is nothing to show.",
  },

  /* ------------------------------------------- новый код взамен потерянного */

  /* Код показывается один раз и нигде не хранится — а значит, потеряв бумажку
     при живом открытом телефоне, человек больше никогда не смог бы записать
     его заново, и пространство осталось бы на одной невосстановимой копии. */
  reissue: { ru: "Выпустить новый код", en: "Issue a new code" },
  reissueTitle: {
    ru: "Старый код перестанет работать",
    en: "The old code will stop working",
  },
  reissueOldDies: {
    ru: "Как только появится новый код, прежний перестанет открывать пространство. Если он записан в менеджере паролей или на бумаге — замените его, иначе там останется мёртвая запись вместо рабочей.",
    en: "The moment a new code exists, the old one stops opening the space. If it is in a password manager or on paper, replace it — otherwise what is written there is a dead code standing in for a working one.",
  },
  reissueSafe: {
    ru: "Само содержимое не тронется: ключ остаётся прежним, перешифровывать ничего не нужно.",
    en: "Nothing in the space is touched: the key stays as it is, and nothing has to be re-encrypted.",
  },
  reissueConfirm: { ru: "Выпустить", en: "Issue it" },
  reissueFailed: {
    ru: "Новый код не выпустился. Прежний по-прежнему работает.",
    en: "The new code was not issued. The old one still works.",
  },

  /* ------------------------------------------------------------------ замок */

  lockedTitle: { ru: "Пространство закрыто", en: "This space is locked" },
  lockedBody: {
    ru: "Оно зашифровано, а ключа на этом устройстве нет. Списки, переписка и календарь останутся нечитаемыми, пока пространство не открыто.",
    en: "It is encrypted and this device has no key. Lists, chat and calendar stay unreadable until the space is opened.",
  },
  lockedScan: {
    ru: "Проще всего — на телефоне, где пространство уже открыто, откройте «Настройки» → «Показать ключ для второго устройства» и наведите сюда камеру.",
    en: "The simplest way: on a phone where the space is already open, go to Settings → “Show the key for a second device” and point this camera at it.",
  },
  /* Сканер живёт здесь же, а не отсылает к системной камере: экран, который
     говорит «наведите камеру» и не даёт её навести, — это тупик. */
  scan: { ru: "Сканировать квадрат", en: "Scan the square" },
  scanHint: {
    ru: "Наведите на квадрат с другого телефона — он откроется сам.",
    en: "Point it at the square on the other phone — it opens by itself.",
  },
  scanDenied: {
    ru: "Камера недоступна. Возможно, доступ к ней не разрешён — это можно поправить в настройках браузера.",
    en: "No camera. Access may not be allowed — that can be changed in the browser's settings.",
  },
  scanUnavailable: {
    ru: "Этот браузер не даёт приложению камеру.",
    en: "This browser does not give the app a camera.",
  },
  scanUseCode: {
    ru: "Ничего страшного — ниже можно ввести код восстановления.",
    en: "No matter — the recovery code below does the same thing.",
  },
  scanWrongSpace: {
    ru: "Этот квадрат — от другого пространства.",
    en: "That square belongs to a different space.",
  },
  scanNotOurs: {
    ru: "Это не ключ от пространства.",
    en: "That is not a space key.",
  },

  lockedOr: { ru: "Или введите код восстановления", en: "Or type the recovery code" },
  codeField: { ru: "Код восстановления", en: "Recovery code" },
  codeHint: {
    ru: "Регистр, пробелы и дефисы значения не имеют.",
    en: "Case, spaces and dashes make no difference.",
  },
  unlock: { ru: "Открыть", en: "Open it" },
  unlocking: { ru: "Открываем…", en: "Opening…" },
  wrongCode: { ru: "Код не подходит", en: "That code does not fit" },
  unlockOffline: {
    ru: "Нет связи с сервером — открыть пространство сейчас не получится.",
    en: "There is no connection to the server, so the space cannot be opened right now.",
  },

  /* ---------------------------------------------------------- приём по ссылке */

  joinTitle: { ru: "Открываем пространство", en: "Opening the space" },
  joinWorking: { ru: "Секунду…", en: "One moment…" },
  joinAccepting: { ru: "Принимаем приглашение…", en: "Accepting the invitation…" },

  joinSignInTitle: { ru: "Сначала войдите", en: "Sign in first" },
  joinSignInBody: {
    ru: "Ключ нельзя положить в пространство, пока непонятно, чьё оно. Войдите — и мы продолжим сами, сканировать заново не придётся.",
    en: "A key cannot be put into a space until we know whose it is. Sign in and we will carry on — no need to scan again.",
  },
  joinSignInAction: { ru: "Войти", en: "Sign in" },
  /* Квадрат показывают тому, кого зовут, — то есть почти всегда человеку без
     учётной записи. Регистрация впереди входа, и код едет с ней. */
  joinRegisterAction: { ru: "Завести учётную запись", en: "Create an account" },
  joinRegisterBody: {
    ru: "Приглашение уже в квадрате — код вводить не придётся. Заведите учётную запись, и пространство откроется само.",
    en: "The invitation is already in the square — there is no code to type. Create an account and the space opens by itself.",
  },

  joinBadLinkTitle: { ru: "В ссылке нет ключа", en: "There is no key in this link" },
  joinBadLinkBody: {
    ru: "Похоже, адрес скопировали руками или он открылся второй раз: ключ живёт после решётки и стирается сразу, как только страница его прочитает. Отсканируйте квадрат ещё раз.",
    en: "It looks like the address was copied by hand, or opened a second time: the key lives after the “#” and is wiped the moment the page reads it. Scan the square again.",
  },
  joinBrokenKeyTitle: { ru: "Ключ не подошёл", en: "The key did not fit" },
  joinBrokenKeyBody: {
    ru: "Прочитать его не вышло. Так бывает, когда квадрат снят не целиком или показан от другого пространства.",
    en: "It could not be read. That happens when the square was captured in part, or belongs to a different space.",
  },
  joinNotMemberTitle: { ru: "Вас ещё не пригласили", en: "You have not been invited yet" },
  joinNotMemberBody: {
    ru: "Ключ есть, а участником пространства вы пока не значитесь. Попросите пригласить вас — и отсканируйте квадрат ещё раз.",
    en: "There is a key, but you are not a member of this space yet. Ask to be invited, then scan the square again.",
  },
  joinFailedTitle: { ru: "Не получилось", en: "It did not work" },
  joinRetryHint: {
    ru: "Ключ на устройство не лёг. Попробуйте отсканировать квадрат ещё раз.",
    en: "No key reached this device. Try scanning the square again.",
  },
  joinBack: { ru: "К спискам", en: "Back to the lists" },
});
